import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { CompactHeader } from "@/components/booking/CompactHeader";
import { ConsentBanner } from "@/components/ConsentBanner";
import { useLang, useT, detectBrowserLangSuggestion, swapLangInPath } from "@/i18n";

/**
 * Full-screen immersive shell shared by every diagnostic (EP
 * Communication Diagnostic, Presentation Readiness Check, Presenter
 * Archetype) — mirrors the exact pattern already used by
 * `HighStakesAudit`/`ExerciseHub` (standalone route outside
 * `<SiteLayout/>`: no Nav, no Footer, no sticky CTAs). Only chrome is
 * the same minimal `CompactHeader` those booking pages already use
 * (logo + language switcher).
 *
 * Bug fix (2026-02, pass 37) — this shell used to also skip the
 * `<ConsentBanner/>` that every `SiteLayout`-wrapped page shows, so a
 * visitor landing DIRECTLY on a diagnostic (the common case — ads,
 * search, a direct link) could never explicitly grant/decline
 * analytics consent during that visit, which was the dominant reason
 * `diagnostic_completed` was being suppressed before ever reaching
 * PostHog (see `posthog_tracking_dispatch.py`'s consent gate). Added
 * back here, ONCE, so all 3 diagnostics get it identically. It is the
 * SAME component/same persisted `iag_consent_v1` state the rest of the
 * site uses — it renders `null` once the visitor has already decided
 * (`ConsentBanner.jsx`: `if (decided) return null`), never reappears
 * for a returning visitor, and a "Reject"/undecided state means
 * `capture()` (analytics.js) keeps silently no-op'ing exactly as
 * before — nothing here weakens or bypasses that gate. It is a
 * bottom-anchored, non-blocking banner (own `pointer-events-auto`
 * only on its own card, the diagnostic underneath stays fully usable)
 * — never a modal, never prevents starting/finishing a diagnostic.
 */
export const DiagnosticLayout = ({ children, showExit = false, onExit, showLanguagePrompt = false }) => {
  const t = useT();
  const { lang } = useLang();
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [langSuggestion, setLangSuggestion] = useState(null);

  useEffect(() => {
    if (!showLanguagePrompt) return;
    const suggestion = detectBrowserLangSuggestion(lang);
    if (!suggestion) return;
    const dismissedKey = `lang_suggestion_dismissed_${suggestion.code}`;
    if (sessionStorage.getItem(dismissedKey)) return;
    setLangSuggestion(suggestion);
  }, [showLanguagePrompt, lang]);

  const dismissLangSuggestion = () => {
    if (langSuggestion) sessionStorage.setItem(`lang_suggestion_dismissed_${langSuggestion.code}`, "1");
    setLangSuggestion(null);
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="diagnostic-shell">
      <CompactHeader />

      {showLanguagePrompt && langSuggestion && (
        <div
          data-testid="diagnostic-lang-suggestion"
          className="bg-ink-soft/10 border-b border-ink-soft/20 px-5 md:px-10 py-2.5 flex items-center justify-between gap-4 text-sm"
        >
          <span className="font-body text-ink-soft">{t("diagnosticUi.lang_suggestion", { lang: langSuggestion.label })}</span>
          <div className="flex items-center gap-4 shrink-0">
            <Link
              to={swapLangInPath(window.location.pathname, langSuggestion.code)}
              data-testid="diagnostic-lang-suggestion-switch"
              onClick={dismissLangSuggestion}
              className="font-body font-medium text-terracotta hover:underline underline-offset-4"
            >
              {t("diagnosticUi.lang_suggestion_switch")}
            </Link>
            <button
              type="button"
              data-testid="diagnostic-lang-suggestion-dismiss"
              onClick={dismissLangSuggestion}
              aria-label={t("diagnosticUi.lang_suggestion_dismiss")}
              className="text-ink-soft hover:text-ink transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {showExit && (
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex justify-end py-2.5">
            <button
              type="button"
              data-testid="diagnostic-exit-button"
              onClick={() => setConfirmingExit(true)}
              className="p-2 rounded-full text-ink-soft hover:text-terracotta hover:bg-ink/5 transition-colors"
              aria-label="Exit"
            >
              <X size={20} />
            </button>
          </div>

          {confirmingExit && (
            <div
              data-testid="diagnostic-exit-confirm"
              className="absolute right-5 md:right-10 top-full z-30 mt-1 w-72 rounded-xl border border-ink/10 bg-paper shadow-xl p-5 text-center"
            >
              <p className="font-body text-sm text-ink mb-4">{t("diagnosticUi.exit_confirm_text")}</p>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  data-testid="diagnostic-exit-confirm-stay"
                  onClick={() => setConfirmingExit(false)}
                  className="px-4 py-2 rounded-full font-body text-sm font-medium bg-terracotta text-paper hover:bg-terracotta-dark transition-colors"
                >
                  {t("diagnosticUi.exit_confirm_stay")}
                </button>
                <button
                  type="button"
                  data-testid="diagnostic-exit-confirm-leave"
                  onClick={onExit}
                  className="px-4 py-2 rounded-full font-body text-sm font-medium border border-ink/30 text-ink hover:border-terracotta hover:text-terracotta transition-colors"
                >
                  {t("diagnosticUi.exit_confirm_leave")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {children}
      <ConsentBanner />
    </div>
  );
};

export default DiagnosticLayout;
